'use client';

/**
 * Payment React Hooks
 * Provider-neutral card operations over our `/api/v1/payments` routes
 * Follows the established architecture patterns from useAdminAuth, useUserAuth, and useVercel
 */

import {
    // Auth models
    AuthResponse,
    Neutral,
    // Capture models
    CaptureResponse,
    // Void models
    VoidResponse,
    type IAuthRequest,
    type ICaptureRequest,
} from '@order-up/models';
import type { INeutralPostAuthResult } from '../services/paymentsApi';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    paymentsApiService,
    type ITipAdjustRequest,
    type ITipAdjustResponse,
    type ITipCorrectionRequest,
    type ITipCorrectionResponse,
} from '../services/paymentsApi';
import { createLogger } from '../utils/logger';

export type { ITipAdjustRequest, ITipAdjustResponse, ITipCorrectionRequest, ITipCorrectionResponse };

const logger = createLogger('usePayments');

// ========================================
// Query Keys
// ========================================

/**
 * Defines the query keys used for payment operations
 */
export const paymentsKeys = {
    all: ['payments'] as const,

    // Transaction operations
    auth: () => [...paymentsKeys.all, 'auth'] as const,
    capture: () => [...paymentsKeys.all, 'capture'] as const,
    refund: () => [...paymentsKeys.all, 'refund'] as const,
    void: () => [...paymentsKeys.all, 'void'] as const,
    tipAdjust: () => [...paymentsKeys.all, 'tipAdjust'] as const,
    tipCorrection: () => [...paymentsKeys.all, 'tipCorrection'] as const,

    // Inquire operations (invalidated after any money movement)
    inquire: () => [...paymentsKeys.all, 'inquire'] as const,
};

// ========================================
// Authorization Hooks
// ========================================

/**
 * Hook to authorize a payment transaction
 */
export function useAuthorizePayment() {
    const queryClient = useQueryClient();

    return useMutation<AuthResponse, Error, IAuthRequest>({
        mutationFn: async (request: IAuthRequest) => {
            logger.debug('Starting authorization', { orderId: request.orderid });
            return await paymentsApiService.authorize(request);
        },
        onSuccess: (data) => {
            logger.info('Authorization successful', { retref: data.retref });

            // Invalidate inquire queries since we have a new transaction
            queryClient.invalidateQueries({ queryKey: paymentsKeys.inquire() });
        },
        onError: (error, _variables) => {
            logger.error('Authorization failed', error);
            logger.debug('Failed request', { hasOrderId: !!_variables.orderid });
        },
    });
}

/**
 * Hook to authorize a payment using the neutral AuthorizeRequest schema.
 *
 * Phase 3: Use this hook for new callers. It sends a provider-agnostic
 * AuthorizeRequest to the /payments/auth endpoint (Phase 1 neutral route).
 * The server dispatches to the location's processor from its
 * providerRouting config.
 *
 * Migrate legacy callers from useAuthorizePayment() (IAuthRequest) to this hook.
 */
export function useNeutralAuthorize() {
    const queryClient = useQueryClient();

    return useMutation<AuthResponse, Error, Neutral.AuthorizeRequest>({
        mutationFn: async (request: Neutral.AuthorizeRequest) => {
            logger.debug('Starting neutral authorization', { orderId: request.orderId });
            return await paymentsApiService.neutralAuthorize(request);
        },
        onSuccess: (data) => {
            logger.info('Neutral authorization successful', { retref: data.retref });
            queryClient.invalidateQueries({ queryKey: paymentsKeys.inquire() });
        },
        onError: (error) => {
            logger.error('Neutral authorization failed', error);
        },
    });
}

// ========================================
// Capture Hooks
// ========================================

/**
 * Hook to capture an authorized payment
 */
export function useCapturePayment() {
    const queryClient = useQueryClient();

    return useMutation<CaptureResponse, Error, ICaptureRequest>({
        mutationFn: async (request: ICaptureRequest) => {
            logger.debug('Starting capture', { retref: request.retref });
            return await paymentsApiService.capture(request);
        },
        onSuccess: (data) => {
            logger.info('Capture successful', { retref: data.retref });

            // Invalidate inquire queries since transaction status changed
            queryClient.invalidateQueries({ queryKey: paymentsKeys.inquire() });
        },
        onError: (error, _variables) => {
            logger.error('Capture failed', error);
            logger.debug('Failed request', { retref: _variables.retref });
        },
    });
}

// ========================================
// Refund Hooks
// ========================================

/**
 * Hook to refund a captured payment
 */
export function useRefundPayment() {
    const queryClient = useQueryClient();

    // Uses the neutral POST /payments/refund route (provider resolved
    // server-side from tenantId/restaurantId, which the route REQUIRES).
    return useMutation<INeutralPostAuthResult, Error, {
        retref: string;
        tenantId: number;
        restaurantId: number;
        amount?: string;
        idempotencyKey?: string;
    }>({
        mutationFn: async (request) => {
            logger.debug('Starting refund', { retref: request.retref });
            return await paymentsApiService.refundPayment(request);
        },
        onSuccess: (data) => {
            logger.info('Refund successful', { retref: data.retref });

            // Invalidate inquire queries since transaction status changed
            queryClient.invalidateQueries({ queryKey: paymentsKeys.inquire() });
        },
        onError: (error, _variables) => {
            logger.error('Refund failed', error);
            logger.debug('Failed request', { retref: _variables.retref });
        },
    });
}

// ========================================
// Void Hooks
// ========================================

/**
 * Hook to void an authorized or captured payment by retrieval reference.
 *
 * Uses the neutral POST /payments/void route, which takes the processor
 * reference (a TRX Reference.Guid is 15-char alphanumeric).
 *
 * Requires `tenantId` and `restaurantId` for provider routing.
 */
export function useVoidPayment() {
    const queryClient = useQueryClient();

    return useMutation<INeutralPostAuthResult, Error, {
        retref: string;
        tenantId: number;
        restaurantId: number;
        amount?: string;
        idempotencyKey?: string;
        voidReasonId?: number;
        voidReasonText?: string;
        authorizedBy?: number;
    }>({
        mutationFn: async (request) => {
            logger.debug('Starting void', { retref: request.retref });
            return await paymentsApiService.voidPayment(request);
        },
        onSuccess: (data) => {
            logger.info('Void successful', { retref: data.retref });

            // Invalidate inquire queries since transaction status changed
            queryClient.invalidateQueries({ queryKey: paymentsKeys.inquire() });
        },
        onError: (error, _variables) => {
            logger.error('Void failed', error);
            logger.debug('Failed request', { retref: _variables.retref });
        },
    });
}

// ========================================
// Tip Adjustment Hooks
// ========================================

/**
 * Hook to adjust the tip on a captured payment before settlement.
 *
 * The new `tipAmount` (dollars) REPLACES the previous tip — the server
 * re-captures the transaction at base + tip. Same idempotency-key
 * conventions as void/refund: mint the key once per user intent
 * (dialog-open) via `generateIdempotencyKey('tip-adjust', retref)` and
 * reuse it on retries of the same intent.
 */
export function useTipAdjustPayment() {
    const queryClient = useQueryClient();

    return useMutation<ITipAdjustResponse, Error, ITipAdjustRequest>({
        mutationFn: async (request: ITipAdjustRequest) => {
            logger.debug('Starting tip adjust', { retref: request.retref });
            return await paymentsApiService.tipAdjust(request);
        },
        onSuccess: (data) => {
            logger.info('Tip adjust successful', { retref: data.retref });

            // Invalidate inquire queries since transaction amount changed
            queryClient.invalidateQueries({ queryKey: paymentsKeys.inquire() });
        },
        onError: (error, _variables) => {
            logger.error('Tip adjust failed', error);
            logger.debug('Failed request', { retref: _variables.retref });
        },
    });
}

/**
 * Hook to CORRECT the tip on a payment that has already been captured — a
 * tip-only Return of the difference (docs/POST_PAYMENT_ADJUSTMENT.md §7). The
 * open-hold case is `useTipAdjustPayment`; the server refuses each with the
 * other named. Mint the key once per dialog-open via
 * `generateIdempotencyKey('tip-correction', retref)`.
 */
export function useTipCorrectionPayment() {
    const queryClient = useQueryClient();

    return useMutation<ITipCorrectionResponse, Error, ITipCorrectionRequest>({
        mutationKey: paymentsKeys.tipCorrection(),
        mutationFn: async (request: ITipCorrectionRequest) => {
            logger.debug('Starting tip correction', { retref: request.retref });
            return await paymentsApiService.tipCorrection(request);
        },
        onSuccess: (data) => {
            logger.info('Tip correction finished', {
                retref: data.retref,
                approved: data.tipCorrection?.approved,
            });
            // Money went back to the card — refundable / voidable changed.
            queryClient.invalidateQueries({ queryKey: paymentsKeys.inquire() });
        },
        onError: (error, _variables) => {
            logger.error('Tip correction failed', error);
            logger.debug('Failed request', { retref: _variables.retref });
        },
    });
}

// Customer saved cards are provider-agnostic: list/remove via
// `useSavedCards` / `useDeleteSavedCard`; saving happens at checkout. Reuse
// `savedCardsService` for imperative calls.
